
import { executeWithTimeout } from '@/lib/supabase';
import { supabase } from '@/lib/supabase/client';
import { DEFAULT_ROLE } from './constants';
import { getRoleFromRPC, checkDealerStatus } from './roleUtils';
import { createUserRoleViaRPC, insertUserRoleDirectly } from './roleCreationService';
import { debugLog, debugError } from '@/utils/debugLogger';

/**
 * Service for user role-related operations
 */
export const roleService = {
  /**
   * Fetch user role information with parallel RPC calls to avoid waterfall timeouts.
   * Skips getRoleFromSessionMetadata() — redundant since caller already has session data.
   */
  fetchUserRole: async (userId: string) => {
    debugLog('auth', 'Fetching role for user ID:', userId, 'RoleService');
    
    if (!userId) {
      debugLog('auth', 'No userId provided, returning default role', undefined, 'RoleService');
      return DEFAULT_ROLE;
    }
    
    try {
      // Race both RPC approaches in parallel with a single 5s timeout
      const rpcPromise1 = getRoleFromRPC(userId).catch(() => null);
      const rpcPromise2 = (async () => {
        try {
          const { data, error } = await supabase.rpc('get_user_role', { user_id_param: userId });
          return !error && data ? data : null;
        } catch { return null; }
      })();

      const rpcRace = Promise.race([rpcPromise1, rpcPromise2]);

      const timeoutPromise = new Promise<null>(resolve =>
        setTimeout(() => resolve(null), 5000)
      );

      const rpcRole = await Promise.race([rpcRace, timeoutPromise]);
      
      if (rpcRole) {
        debugLog('auth', 'Using role from parallel RPC:', rpcRole, 'RoleService');
        return rpcRole;
      }
      
      // Final fallback: check dealer status
      const dealerRole = await checkDealerStatus(userId, executeWithTimeout);
      if (dealerRole) {
        debugLog('auth', 'User is a dealer, assigning DEALER role', undefined, 'roleService');
        return dealerRole;
      }

      debugLog('auth', 'Falling back to default USER role', undefined, 'roleService');
      return DEFAULT_ROLE;
    } catch (error) {
      debugError('auth', 'Critical error in fetchUserRole:', error, 'roleService');
      return DEFAULT_ROLE;
    }
  },
  
  /**
   * Create default user role
   */
  createUserRole: async (userId: string) => {
    debugLog('auth', 'Creating default role for user:', userId, 'roleService');
    
    if (!userId) {
      debugLog('auth', 'No userId provided, skipping role creation', undefined, 'roleService');
      return;
    }

    // Global 8s timeout to prevent hanging even in fire-and-forget
    const timeout = new Promise<void>(resolve => setTimeout(() => {
      debugLog('auth', 'createUserRole global timeout (8s)', undefined, 'roleService');
      resolve();
    }, 8000));

    const work = (async () => {
      // First check if the user already has a role using RPC
      try {
        const { data, error } = await executeWithTimeout(
          async () => await supabase
            .rpc('get_user_role', { user_id_param: userId }),
          5000
        );
        
        if (!error && data && data !== 'user') {
          debugLog('auth', 'User already has role:', data, 'roleService');
          return;
        }
      } catch (checkError) {
        debugLog('auth', 'Error checking existing role:', checkError, 'roleService');
      }
      
      // User doesn't have a role, create one
      try {
        try {
          debugLog('auth', 'Attempting to create role via RPC', undefined, 'roleService');
          const roleCreatedViaRPC = await createUserRoleViaRPC(userId);
          if (roleCreatedViaRPC) {
            debugLog('auth', 'Role created via RPC', undefined, 'roleService');
            return;
          }
        } catch (rpcError) {
          debugLog('auth', 'Error creating role via RPC:', rpcError, 'roleService');
        }
        
        debugLog('auth', 'Attempting direct role insertion', undefined, 'roleService');
        await insertUserRoleDirectly(userId);
        debugLog('auth', 'Role created via direct insertion', undefined, 'roleService');
      } catch (roleError) {
        debugError('auth', 'Exception during role creation:', roleError, 'roleService');
      }
    })();

    await Promise.race([work, timeout]);
  }
};
